import { avoidRateLimit } from "@cm-websites/common";
import { Box, Container, Typography } from "@mui/material";
import { cmsApiClient, CmsApiAcl } from "@cm-websites/common/dist/cms-api";
import { NavigationAndFooterLayout } from "../../layouts";
import { NextSeo } from "next-seo";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import i18nConfig from "../../../next-i18next.config";
import React from "react";
import type { CmsApiModel } from "@cm-websites/common/dist/cms-api";
import type { GetStaticProps, GetStaticPaths } from "next";
import type { NextPageWithLayout, Product } from "@cm-websites/common";

type RecipeProps = { product: Product };

export const Recipe: NextPageWithLayout<RecipeProps> = ({ product }) => {
  const { t } = useTranslation(["c-product", "c-common"]);
  const categoryPath = t("c-common:slug-print");

  return (
    <React.Fragment>
      <NextSeo
        canonical={`/${categoryPath}/${product.slug}`}
        description={`Menu suggestion for our Murder Mystery ${product.title}`}
        title={`Menu suggestion for „${product.title}“ | Murder Mystery Party Games`}
      />

      <Box sx={{ pb: { xs: 8, sm: 10, lg: 14, xl: 16 } }}>
        <Container
          maxWidth="xl"
          sx={{ pt: { xs: 12, sm: 14, lg: 16 }, pb: { xs: 6, sm: 8, lg: 10, xl: 12 } }}
        >
          <Typography variant="h1" sx={{ hyphens: "none", pb: 4 }}>
            <Typography component="span" variant="overline" sx={{ width: "100%" }}>
              Menu suggestion
            </Typography>
            {product.title}
          </Typography>

          <Typography variant="body1" sx={{ pb: 6 }}>
            This is our menu suggestion for “{product.title}”. Please feel free to modify the menu
            according to your own taste or replace it with another one.
          </Typography>

          <Typography variant="h2" sx={{ pb: 1 }}>
            Starter
          </Typography>
          <Typography
            component="div"
            dangerouslySetInnerHTML={{ __html: product.additionalMaterials.recipes?.starter ?? "" }}
            paragraph
            sx={{ pb: 3 }}
            variant="body1"
          />

          <Typography variant="h2" sx={{ pb: 1 }}>
            Main course
          </Typography>
          <Typography
            component="div"
            dangerouslySetInnerHTML={{ __html: product.additionalMaterials.recipes?.main ?? "" }}
            paragraph
            sx={{ pb: 3 }}
            variant="body1"
          />

          <Typography variant="h2" sx={{ pb: 1 }}>
            Dessert
          </Typography>
          <Typography
            component="div"
            dangerouslySetInnerHTML={{ __html: product.additionalMaterials.recipes?.dessert ?? "" }}
            paragraph
            sx={{ pb: 3 }}
            variant="body1"
          />
        </Container>
      </Box>
    </React.Fragment>
  );
};

Recipe.getLayout = (page) => <NavigationAndFooterLayout>{page}</NavigationAndFooterLayout>;

export default Recipe;

export const getStaticProps: GetStaticProps = async (context) => {
  const fields = [
    "*.*",
    "relatedCases.related_Culinario_Mortale_id.*.*",
    "printVersionImages.*",
    "printVersionImages.directus_files_id.mask",
    "printVersionImages.directus_files_id.id",
    "printVersionImages.directus_files_id.description",
    "onlineVersionImages.*",
    "onlineVersionImages.directus_files_id.mask",
    "onlineVersionImages.directus_files_id.id",
    "onlineVersionImages.directus_files_id.description",
  ];

  await avoidRateLimit();
  const slug = context.params?.slug;
  if (!slug) return { notFound: true };

  const { data } = await cmsApiClient.get<{
    data: CmsApiModel.components["schemas"]["ItemsCulinarioMortale"][];
  }>(
    `/items/Culinario_Mortale?filter={ "slug": { "_eq": "${slug}" }, "language": "GB"}&fields=${fields.join(
      ","
    )}`
  );

  const product = (await CmsApiAcl.product(data.data[0], "print", { origin: "rezeptideen slug" })) ?? [];

  return {
    props: {
      ...(context.locale
        ? await serverSideTranslations(
            context.locale,
            ["c-common", "c-user-consent-management"],
            i18nConfig
          )
        : {}),
      product,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await cmsApiClient.get<{
    data: CmsApiModel.components["schemas"]["ItemsCulinarioMortale"][];
  }>(`/items/Culinario_Mortale?filter={"language": "GB"}&fields=*`);

  const pathsData = data.data.map((product) => {
    if (!product.id) throw new Error(`Expected product.id`);
    if (!product.slug) throw new Error(`Expected product.slug`);
    return { params: { slug: product.slug } };
  });

  return {
    paths: pathsData,
    fallback: false,
  };
};
